import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Loader2, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Modal disparado al mover un activo a la fase "En Gestión" (spec Feb 2026).
 *
 * Regla: para entrar en "En Gestión" el activo debe tener al menos una
 * tarea pendiente con responsable asignado. Si ya la tiene, el cambio de
 * fase se confirma directamente sin mostrar el formulario. Si no, se abre
 * el formulario de tarea con el aviso correspondiente; la tarea y el
 * cambio de fase se guardan juntos — si se cancela, el activo permanece en
 * su fase anterior (no se crea nada).
 *
 * Props: open, asset ({ id, ... }), onClose(), onConfirmed(updatedAsset?).
 */
const TareaGestionModal = ({ open, asset, onClose, onConfirmed }) => {
  const { user } = useAuth();
  const [checking, setChecking] = useState(true);
  const [users, setUsers] = useState([]);
  const [tarea, setTarea] = useState('');
  const [responsableId, setResponsableId] = useState('');
  const [fecha, setFecha] = useState('');
  const [hora, setHora] = useState('');
  const [notas, setNotas] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !asset) return;
    let active = true;
    setTarea(''); setFecha(''); setHora(''); setNotas('');
    setResponsableId(user?.id || '');
    setChecking(true);
    (async () => {
      try {
        const [qualifiesRes, usersRes] = await Promise.all([
          axios.get(`${API}/assets/${asset.id}/tasks/qualifies-for-en-gestion`, { withCredentials: true }),
          axios.get(`${API}/assets/${asset.id}/task-assignable-users`, { withCredentials: true }),
        ]);
        if (!active) return;
        setUsers(usersRes.data?.items ?? usersRes.data ?? []);
        if (qualifiesRes.data?.qualifies) {
          // Ya tiene una tarea pendiente con responsable: confirma directo.
          await axios.patch(
            `${API}/assets/${asset.id}/quick-update`,
            { etapa_visual: 'en_gestion' },
            { withCredentials: true },
          );
          toast.success('Fase actualizada a En Gestión');
          onConfirmed?.();
          return;
        }
        setChecking(false);
      } catch (err) {
        if (!active) return;
        toast.error(err.response?.data?.detail || 'No se pudo comprobar las tareas del activo');
        onClose?.();
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, asset?.id]);

  if (!open || !asset) return null;

  const canSubmit = tarea.trim() && responsableId && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/assets/${asset.id}/tasks`,
        { tarea: tarea.trim(), responsable_id: responsableId, fecha: fecha || null, hora: hora || null, notas_iniciales: notas.trim() || null },
        { withCredentials: true },
      );
      await axios.patch(
        `${API}/assets/${asset.id}/quick-update`,
        { etapa_visual: 'en_gestion' },
        { withCredentials: true },
      );
      toast.success('Tarea creada y fase actualizada a En Gestión');
      onConfirmed?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
      data-testid="tarea-gestion-modal-overlay"
    >
      <div
        className="rounded-xl shadow-2xl max-w-lg w-full max-h-[85vh] flex flex-col"
        style={{ background: 'var(--color-bg-elevated)' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="tarea-gestion-modal"
      >
        <div className="flex items-start justify-between p-5 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-fuchsia-500 text-white">
              <ClipboardList className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold text-base" style={{ color: 'var(--color-text-primary)' }}>
                Pasar a fase En Gestión
              </h3>
              <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                Para pasar este activo a En Gestión, asigna al menos una tarea pendiente y un responsable.
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-700/20 rounded" aria-label="Cerrar" data-testid="tarea-gestion-modal-close">
            <X className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
          </button>
        </div>

        {checking ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-fuchsia-500" />
          </div>
        ) : (
          <div className="overflow-y-auto flex-1 p-4 space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wide font-semibold mb-1 block" style={{ color: 'var(--color-text-muted)' }}>
                Tarea <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={tarea}
                onChange={(e) => setTarea(e.target.value)}
                className="input-base w-full"
                placeholder="Ej. Llamar al notario para firma"
                data-testid="tarea-gestion-input-tarea"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide font-semibold mb-1 block" style={{ color: 'var(--color-text-muted)' }}>
                Usuario responsable <span className="text-red-500">*</span>
              </label>
              <select
                value={responsableId}
                onChange={(e) => setResponsableId(e.target.value)}
                className="input-base w-full"
                data-testid="tarea-gestion-select-responsable"
              >
                <option value="">Selecciona un responsable</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name || u.email}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wide font-semibold mb-1 block" style={{ color: 'var(--color-text-muted)' }}>
                  Fecha (opcional)
                </label>
                <input
                  type="date"
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                  className="input-base w-full"
                  data-testid="tarea-gestion-input-fecha"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide font-semibold mb-1 block" style={{ color: 'var(--color-text-muted)' }}>
                  Hora (opcional)
                </label>
                <input
                  type="time"
                  value={hora}
                  onChange={(e) => setHora(e.target.value)}
                  disabled={!fecha}
                  className="input-base w-full disabled:opacity-50"
                  data-testid="tarea-gestion-input-hora"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide font-semibold mb-1 block" style={{ color: 'var(--color-text-muted)' }}>
                Notas iniciales (opcional)
              </label>
              <textarea
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
                rows={2}
                className="input-base w-full resize-none"
                data-testid="tarea-gestion-input-notas"
              />
            </div>
            {!fecha && (
              <p className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}>
                Sin fecha, la tarea queda como "Pendiente de agendar".
              </p>
            )}
          </div>
        )}

        {!checking && (
          <div className="p-4 border-t flex items-center justify-end gap-2" style={{ borderColor: 'var(--color-border)' }}>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium rounded border"
              style={{ color: 'var(--color-text-primary)', borderColor: 'var(--color-border)', background: 'transparent' }}
              data-testid="tarea-gestion-modal-cancel"
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className={`px-4 py-2 text-sm font-semibold rounded flex items-center gap-2 transition-colors ${
                canSubmit ? 'bg-fuchsia-600 hover:bg-fuchsia-700 text-white' : 'bg-slate-300 text-slate-500 cursor-not-allowed'
              }`}
              data-testid="tarea-gestion-modal-submit"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Crear tarea y pasar a En Gestión
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TareaGestionModal;
